/*
 * @Author: Ryan
 * @Date: 2026-01-09
 * @Description: Use Cases Section for Video Translator landing page (手风琴式 Tab 布局)
 */
import React, { useEffect, useRef, useState } from 'react';

import styles from './index.module.scss';

// 播放按钮 SVG
const PlayIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
  >
    <rect
      x="0.5"
      y="0.5"
      width="63"
      height="63"
      rx="31.5"
      fill="white"
      fillOpacity="0.1"
    />
    <rect x="0.5" y="0.5" width="63" height="63" rx="31.5" stroke="white" />
    <path
      d="M41.5038 30.3359C42.6913 31.1275 42.6913 32.8725 41.5038 33.6641L29.1094 41.9271C27.7803 42.8131 26 41.8603 26 40.2629L26 23.7371C26 22.1397 27.7803 21.1869 29.1094 22.0729L41.5038 30.3359Z"
      fill="white"
    />
  </svg>
);

// 视频数据（6个用例对应6个视频）
const USE_CASE_VIDEOS = [
  {
    // 原6 → 新1: content-creator
    videoUrl:
      'https://assets.falcocut.ai/tools/video-translator/use-case-video-content-creator.mp4',
    poster:
      'https://assets.falcocut.ai/tools/video-translator/use-case-video-content-creator-cover.webp',
  },
  {
    // 原3 → 新2: global-education
    videoUrl:
      'https://assets.falcocut.ai/tools/video-translator/use-case-of-video-translation-global-education.mp4',
    poster:
      'https://assets.falcocut.ai/tools/video-translator/use-case-of-video-translation-global-education-poster.webp',
  },
  {
    // 原5 → 新3: social-creators
    videoUrl:
      'https://assets.falcocut.ai/tools/video-translator/use-case-of-video-translation-social-creators.mp4',
    poster:
      'https://assets.falcocut.ai/tools/video-translator/use-case-of-video-translation-social-creators-poster.webp',
  },
  {
    // 原1 → 新4: business-marketing
    videoUrl:
      'https://assets.falcocut.ai/tools/video-translator/use-case-of-video-translation-business-marketing.mp4',
    poster:
      'https://assets.falcocut.ai/tools/video-translator/use-case-of-video-translation-business-marketing-poster.webp',
  },
  {
    // 原2 → 新5: event-collaboration
    videoUrl:
      'https://assets.falcocut.ai/tools/video-translator/use-case-of-video-translation-event-collaboration.mp4',
    poster:
      'https://assets.falcocut.ai/tools/video-translator/use-case-of-video-translation-event-collaboration-poster.webp',
  },
  {
    // 原4 → 新6: media-and-podcasts
    videoUrl:
      'https://assets.falcocut.ai/tools/video-translator/use-case-of-video-translation-media-and-podcasts.mp4',
    poster:
      'https://assets.falcocut.ai/tools/video-translator/use-case-of-video-translation-media-and-podcasts-poster.webp',
  },
];

export interface UseCaseItem {
  title: string;
  description: string;
}

export interface UseCasesSectionProps {
  /** 区块标题 */
  title: string;
  /** 区块副标题 */
  subtitle?: string;
  /** 用例列表 */
  useCases: UseCaseItem[];
}

interface UseCaseTabProps {
  useCase: UseCaseItem;
  isActive: boolean;
  onClick: () => void;
}

const UseCaseTab: React.FC<UseCaseTabProps> = ({ useCase, isActive, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${styles.tab} ${isActive ? styles.active : ''}`}
    >
      <h3 className={styles.tabTitle}>{useCase.title}</h3>
      <div className={`${styles.tabContent} ${isActive ? styles.expanded : ''}`}>
        <p className={styles.tabDescription}>{useCase.description}</p>
      </div>
    </button>
  );
};

const UseCasesSection: React.FC<UseCasesSectionProps> = ({
  title,
  subtitle,
  useCases,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleTabClick = (index: number) => {
    setActiveIndex(index);
  };

  // 切换 tab 时重置播放状态
  useEffect(() => {
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.load();
    }
  }, [activeIndex]);

  // 播放/暂停切换
  const handleTogglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play().catch(() => {
          // 自动播放可能被阻止，忽略错误
        });
        setIsPlaying(true);
      }
    }
  };

  const currentVideo = USE_CASE_VIDEOS[activeIndex] || USE_CASE_VIDEOS[0];

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        {/* 标题 */}
        <h2 className={styles.title}>{title}</h2>

        {/* 副标题 */}
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}

        {/* 内容区域 */}
        <div className={styles.content}>
          {/* 左侧视频 */}
          <div className={styles.imageWrapper}>
            <div className={styles.imageContainer} onClick={handleTogglePlay}>
              <video
                ref={videoRef}
                src={currentVideo.videoUrl}
                poster={currentVideo.poster}
                className={styles.video}
                loop
                playsInline
                preload="none"
              />
              {/* 播放按钮 */}
              {!isPlaying && (
                <div className={styles.playButton}>
                  <PlayIcon />
                </div>
              )}
            </div>
          </div>

          {/* 右侧 Tab 列表 */}
          <div className={styles.tabsWrapper}>
            {useCases.map((useCase, index) => (
              <UseCaseTab
                key={index}
                useCase={useCase}
                isActive={index === activeIndex}
                onClick={() => handleTabClick(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
